import React, { useEffect, useState } from 'react'
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Popover,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { TimeInput } from '@mantine/dates'
import { useDisclosure } from '@mantine/hooks'
import { Cross2Icon, ExclamationTriangleIcon } from '@radix-ui/react-icons'

import { ExhibitDesignationSelect } from './ExhibitDesignationSelect'

import { ScriptExhibit } from '@/types'
import {
  formatClockTimeWithSeconds,
  formatTimezone,
  withClockTimeInTimezone,
} from '@/utils/time'

type ExhibitRowProps = {
  exhibit: ScriptExhibit
  timezone: string
  onChange: (patch: Partial<ScriptExhibit>) => void
  onDelete: () => void
}

export const ExhibitRow: React.FC<ExhibitRowProps> = ({
  exhibit,
  timezone,
  onChange,
  onDelete,
}) => {
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false)
  // The inputs keep a local copy so typing is not subject to the async cache
  // update (which would move the caret). They follow the exhibit when it
  // changes from elsewhere.
  const [exhibitNumber, setExhibitNumber] = useState(exhibit.exhibit_number)
  const [description, setDescription] = useState(exhibit.description)
  const markedAtTime = formatClockTimeWithSeconds(exhibit.marked_at, timezone)
  const [markedAt, setMarkedAt] = useState(markedAtTime)
  // Only a warning: a future time is still saved and does not block anything.
  const isMarkedAtInFuture = Date.parse(exhibit.marked_at) > Date.now()
  // Re-renders the row once the time arrives, so the warning clears itself.
  const [clockTick, setClockTick] = useState(0)

  useEffect(
    () => setExhibitNumber(exhibit.exhibit_number),
    [exhibit.exhibit_number],
  )
  useEffect(() => setDescription(exhibit.description), [exhibit.description])
  useEffect(() => setMarkedAt(markedAtTime), [markedAtTime])
  useEffect(() => {
    const delay = Date.parse(exhibit.marked_at) - Date.now()
    if (delay <= 0) {
      return
    }

    const timeout = setTimeout(() => setClockTick(tick => tick + 1), delay)
    return () => clearTimeout(timeout)
    // clockTick re-arms the timer if it fired a moment early.
  }, [exhibit.marked_at, clockTick])

  // Committed on blur rather than per keystroke: the list re-sorts by marked-at
  // time, which would move the row while the reporter is still typing.
  const commitMarkedAt = () => {
    const next = withClockTimeInTimezone(exhibit.marked_at, markedAt, timezone)

    if (next === null || next === exhibit.marked_at) {
      setMarkedAt(markedAtTime)
      return
    }
    onChange({ marked_at: next })
  }

  return (
    <Box
      sx={theme => ({
        position: 'relative',
        padding: theme.spacing.md,
        paddingTop: 24,
        backgroundColor: theme.colors.indigo[0],
        borderRadius: theme.radius.sm,
      })}
    >
      <Box
        // Closer to the corner than the attendee X, so it does not crowd the
        // inputs below.
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <Popover
          opened={confirmOpened}
          onChange={opened => (opened ? openConfirm() : closeConfirm())}
          position="left"
          withArrow
          shadow="md"
          width={240}
        >
          <Popover.Target>
            <ActionIcon
              aria-label="Remove exhibit"
              color="primary"
              variant="light"
              sx={theme => ({
                backgroundColor: theme.colors.indigo[1],
                '&:not(:disabled):hover': {
                  backgroundColor: theme.colors.indigo[2],
                },
              })}
              onClick={openConfirm}
            >
              <Cross2Icon />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack spacing="sm">
              <Text size="sm">
                Remove exhibit
                {exhibit.exhibit_number ? ` "${exhibit.exhibit_number}"` : ''}?
              </Text>
              <Group spacing="sm" position="right">
                <Button variant="default" size="xs" onClick={closeConfirm}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  onClick={() => {
                    closeConfirm()
                    onDelete()
                  }}
                >
                  Remove
                </Button>
              </Group>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Box>
      <Stack align="stretch">
        <Box
          sx={theme => ({
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing.md,
          })}
        >
          <TextInput
            label="Exhibit number"
            withAsterisk
            // Shown, but the value is saved and submitted regardless (a blank
            // number goes to parrot as a placeholder).
            error={
              exhibitNumber.trim() === '' ? 'Exhibit number is required' : null
            }
            value={exhibitNumber}
            onChange={event => {
              setExhibitNumber(event.currentTarget.value)
              onChange({ exhibit_number: event.currentTarget.value })
            }}
            sx={{ flex: '1 1 150px' }}
          />
          {/* Designation and Marked at wrap to their own row together, never
              one without the other. */}
          <Box
            sx={theme => ({
              display: 'flex',
              flex: '2 1 370px',
              minWidth: 370,
              gap: theme.spacing.md,
            })}
          >
            <ExhibitDesignationSelect
              value={exhibit.designation}
              onChange={designation => onChange({ designation })}
            />
            <TimeInput
              label="Marked at"
              withSeconds
              value={markedAt}
              // Only a warning: the message, border and text are orange (see
              // `styles`) rather than the error red.
              error={
                isMarkedAtInFuture ? (
                  <Text
                    span
                    color="orange.5"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <ExclamationTriangleIcon />
                    This time is in the future
                  </Text>
                ) : null
              }
              onChange={event => setMarkedAt(event.currentTarget.value)}
              onBlur={commitMarkedAt}
              rightSectionWidth={76}
              rightSection={
                <Text
                  size={11}
                  color="dimmed"
                  sx={{ position: 'relative', right: 2 }}
                >
                  {formatTimezone(timezone, exhibit.marked_at)}
                </Text>
              }
              styles={theme => ({
                input: {
                  // Doubled selector so it beats Mantine's red invalid style.
                  '&&[data-invalid]': {
                    color: theme.colors.orange[5],
                    borderColor: theme.colors.orange[5],
                  },
                  // The timezone takes the place of the browser's clock button.
                  // Keeps "12:34:56 PM" and the picker button in the input.
                  fontSize: 13,
                },
              })}
              sx={{ flex: '1.2 1 0', minWidth: 0 }}
            />
          </Box>
        </Box>
        <Textarea
          label="Description"
          autosize
          minRows={1}
          // Shows the browser's resize handle in the corner, which marks this
          // as a multi-line field even while it is a single line.
          styles={{ input: { resize: 'vertical' } }}
          value={description}
          onChange={event => {
            setDescription(event.currentTarget.value)
            onChange({ description: event.currentTarget.value })
          }}
        />
      </Stack>
    </Box>
  )
}
